// src/ChatsPage.js - Group chats by conversation (sender/receiver pairs)
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Avatar,
  Button,
  TextField,
  IconButton,
  Collapse,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from './supabaseClient';
import ProfileDialog from './ProfileDialog';
import { normalizeProfileImage } from './utils/imageUtils';

import { prettyStatus } from './labels';
export default function ChatsPage() {
  const [conversations, setConversations] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [dialogPhone, setDialogPhone] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchProfilesForPhones = useCallback(async (phones = []) => {
    if (!phones.length) return new Map();
    const { data } = await supabase
      .from('Profiles')
      .select('id, first_name, last_name, email, phone_number, profile_image, user_type, identity_verified');
    const map = new Map();
    (data || []).forEach(p => {
      map.set(p.phone_number, {
        ...p,
        profile_image_url: normalizeProfileImage(p.profile_image),
      });
    });
    return map;
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('Chats')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Group messages by conversation (sender/receiver pair)
      const conversationMap = new Map();

      data.forEach(message => {
        // Create a consistent key for the conversation pair
        const sender = message.sender_phone_number || '';
        const receiver = message.receiver_phone_number || '';
        
        // Create a consistent key (always use the smaller phone number first)
        const key = sender < receiver 
          ? `${sender}::${receiver}` 
          : `${receiver}::${sender}`;

        if (!conversationMap.has(key)) {
          conversationMap.set(key, {
            key,
            sender_phone_number: sender,
            receiver_phone_number: receiver,
            messages: [],
            lastMessage: null,
            messageCount: 0,
          });
        }

        const conv = conversationMap.get(key);
        conv.messages.push(message);
        conv.messageCount += 1;
        
        // Track the most recent message
        if (!conv.lastMessage || new Date(message.created_at) > new Date(conv.lastMessage.created_at)) {
          conv.lastMessage = message;
        }
      });

      // Fetch profile info for participants
      const phones = Array.from(conversationMap.values())
        .flatMap(c => [c.sender_phone_number, c.receiver_phone_number])
        .filter(Boolean);
      const profileMap = await fetchProfilesForPhones(phones);

      // Group conversations by user (primary participant)
      const conversationsByUser = new Map();
      
      Array.from(conversationMap.values()).forEach(conv => {
        const primaryPhone = conv.sender_phone_number || conv.receiver_phone_number;
        const profile = primaryPhone ? profileMap.get(primaryPhone) : null;
        
        if (!conversationsByUser.has(primaryPhone)) {
          conversationsByUser.set(primaryPhone, {
            phone: primaryPhone,
            profile: profile || null,
            conversations: [],
          });
        }
        
        conversationsByUser.get(primaryPhone).conversations.push({
          ...conv,
          profile,
          avatarUrl: profile?.profile_image_url || null,
          displayName: profile
            ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
            : primaryPhone || 'Unknown User',
          messages: conv.messages.sort((a, b) => 
            new Date(a.created_at) - new Date(b.created_at)
          ),
        });
      });

      // Convert to array and sort by last message time
      const groupedUsers = Array.from(conversationsByUser.values())
        .map(userGroup => ({
          ...userGroup,
          conversations: userGroup.conversations.sort((a, b) => 
            new Date(b.lastMessage?.created_at || 0) - new Date(a.lastMessage?.created_at || 0)
          ),
        }))
        .sort((a, b) => {
          const aLast = a.conversations[0]?.lastMessage?.created_at || '';
          const bLast = b.conversations[0]?.lastMessage?.created_at || '';
          return new Date(bLast) - new Date(aLast);
        });

      setConversations(groupedUsers);
      setAllConversations(groupedUsers);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle conversation selection - show chat view
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
  };

  const closeChatView = () => {
    setSelectedConversation(null);
  };

  // Filter conversations by search term
  useEffect(() => {
    if (!searchTerm) {
      setConversations(allConversations);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allConversations.map(userGroup => {
      const matchesName = `${userGroup.profile?.first_name || ''} ${userGroup.profile?.last_name || ''}`.toLowerCase().includes(term);
      const matchesPhone = userGroup.phone?.toLowerCase().includes(term);
      const matchesEmail = userGroup.profile?.email?.toLowerCase().includes(term);
      
      const filteredConversations = userGroup.conversations.filter(conv => {
        const matchesSender = conv.sender_phone_number?.toLowerCase().includes(term);
        const matchesReceiver = conv.receiver_phone_number?.toLowerCase().includes(term);
        const matchesMessage = conv.messages.some(msg => 
          msg.message?.toLowerCase().includes(term)
        );
        return matchesSender || matchesReceiver || matchesMessage;
      });
      
      if (matchesName || matchesPhone || matchesEmail || filteredConversations.length > 0) {
        return { ...userGroup, conversations: filteredConversations.length > 0 ? filteredConversations : userGroup.conversations };
      }
      return null;
    }).filter(Boolean);

    setConversations(filtered);
  }, [searchTerm, allConversations]);

  const toggleRow = (rowId) => {
    const rowIdString = String(rowId);
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIdString)) {
      newExpanded.delete(rowIdString);
    } else {
      newExpanded.add(rowIdString);
    }
    setExpandedRows(newExpanded);
  };

  const getUserTypeColor = (userType) => {
    switch (userType?.toLowerCase()) {
      case 'stylist':
        return 'primary';
      case 'client':
        return 'success';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeProfile = () => setDialogPhone(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Chats ({conversations.reduce((sum, g) => sum + g.conversations.length, 0)})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage conversations
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={fetchData}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          placeholder="Search chats..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 250 }}
        />
      </Paper>

      {conversations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Conversations Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No conversations found in the database.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          {conversations.map((userGroup, idx) => {
            const rowId = userGroup.phone || `user-${idx}`;
            const isExpanded = expandedRows.has(String(rowId));
            const userName = `${userGroup.profile?.first_name || ''} ${userGroup.profile?.last_name || ''}`.trim() || 'Unknown';
            const profileImage = userGroup.profile?.profile_image_url || userGroup.profile?.profile_image;
            const isVerified = userGroup.profile?.identity_verified === true || userGroup.profile?.identity_verified === 'true' || userGroup.profile?.identity_verified === 1;

            return (
              <Box key={rowId}>
                {/* Summary Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => toggleRow(rowId)}
                >
                  {/* Profile Image */}
                  <Box sx={{ mr: 2, flexShrink: 0 }}>
                    {profileImage ? (
                      <Avatar
                        src={profileImage}
                        alt={userName}
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
                        {userName !== 'Unknown' ? userName.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                    )}
                  </Box>

                  {/* User Info */}
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 150, flexShrink: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {userName}
                      </Typography>
                      {isVerified && (
                        <VerifiedIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, flexShrink: 0 }}>
                      {userGroup.profile?.email || 'N/A'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 150, flexShrink: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (userGroup.phone) setDialogPhone(userGroup.phone);
                      }}
                    >
                      {userGroup.phone || 'N/A'}
                    </Typography>
                    <Chip
                      label={prettyStatus(userGroup.profile?.user_type) || 'N/A'}
                      color={getUserTypeColor(userGroup.profile?.user_type)}
                      size="small"
                      sx={{ minWidth: 80, flexShrink: 0 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, flexShrink: 0 }}>
                      {userGroup.conversations.length} conversation{userGroup.conversations.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {/* Expand/Collapse Icon */}
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Conversations */}
                <Collapse in={isExpanded}>
                  <Box sx={{ p: 3, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Conversations ({userGroup.conversations.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {userGroup.conversations.map((conv) => {
                        const lastMessage = conv.lastMessage;
                        return (
                          <Grid item xs={12} key={conv.key}>
                            <Paper elevation={1} sx={{ p: 2 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Conversation
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {conv.displayName || conv.sender_phone_number || 'Unknown'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Last Message
                                  </Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {lastMessage?.message || 'No messages'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    Messages
                                  </Typography>
                                  <Typography variant="body2">
                                    {conv.messageCount || 0}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    Last Activity
                                  </Typography>
                                  <Typography variant="body2">
                                    {lastMessage ? formatDate(lastMessage.created_at) : 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSelectConversation(conv)}
                                  >
                                    View
                                  </Button>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Paper>
      )}

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={closeProfile}
      />

      {/* Chat View Dialog */}
      <Dialog
        open={Boolean(selectedConversation)}
        onClose={closeChatView}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedConversation?.displayName || 'Conversation'}
            </Typography>
            <IconButton onClick={closeChatView} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedConversation && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <List>
                {selectedConversation.messages.map((msg, idx) => {
                  const isFromSender = msg.sender_phone_number === selectedConversation.sender_phone_number;
                  return (
                    <ListItem
                      key={msg.id || idx}
                      sx={{
                        flexDirection: isFromSender ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <ListItemAvatar sx={{ m: isFromSender ? '0 0 0 1' : '0 1 0 0' }}>
                        <Avatar>
                          {msg.sender_phone_number?.[0] || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={msg.message || '(No text)'}
                        secondary={msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                        sx={{
                          textAlign: isFromSender ? 'right' : 'left',
                          '& .MuiListItemText-primary': {
                            bgcolor: isFromSender ? 'primary.main' : 'grey.200',
                            color: isFromSender ? 'white' : 'text.primary',
                            p: 1.5,
                            borderRadius: 2,
                            display: 'inline-block',
                            maxWidth: '70%',
                            wordBreak: 'break-word',
                          },
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChatView}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
